import React, { useEffect } from 'react';
import { useFormBuilder } from '../../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { 
  FileText, Users, TrendingUp, BarChart3, Plus, Eye, 
  ClipboardList, ArrowRight, Calendar
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const COLORS = ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5'];

const FormBuilderDashboard = ({ onCreateForm, onViewForm, onViewResponses }) => {
  const { dashboardStats, fetchDashboardStats, loading, forms, fetchForms } = useFormBuilder();

  useEffect(() => {
    fetchDashboardStats();
    fetchForms({ page_size: 5 });
  }, []);

  const stats = dashboardStats || {
    total_forms: 0,
    forms_by_status: { draft: 0, live: 0, closed: 0 },
    total_responses: 0,
    responses_this_month: 0,
    most_active_forms: [],
    response_trend: []
  };

  const kpiCards = [
    {
      title: 'Total Forms',
      value: stats.total_forms,
      icon: FileText,
      color: 'bg-orange-500',
      description: `${stats.forms_by_status.live} live, ${stats.forms_by_status.draft} draft`
    },
    {
      title: 'Total Responses',
      value: stats.total_responses,
      icon: ClipboardList,
      color: 'bg-blue-500',
      description: 'All time submissions'
    },
    {
      title: 'This Month',
      value: stats.responses_this_month,
      icon: Calendar,
      color: 'bg-emerald-500',
      description: 'Responses received'
    },
    {
      title: 'Active Forms',
      value: stats.forms_by_status.live,
      icon: TrendingUp,
      color: 'bg-purple-500',
      description: 'Currently accepting responses'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500">Overview of your forms and responses</p>
        </div>
        <Button onClick={onCreateForm} data-testid="create-form-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create New Form
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              {loading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{kpi.title}</p>
                    <p className="text-3xl font-bold mt-1">{kpi.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
                  </div>
                  <div className={`${kpi.color} p-3 rounded-xl`}>
                    <kpi.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Response Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Response Trend</CardTitle>
            <CardDescription>Responses received in the last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[250px] w-full" />
            ) : stats.response_trend.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.response_trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    formatter={(value) => [value, 'Responses']}
                  />
                  <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Most Active Forms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Most Active Forms</CardTitle>
            <CardDescription>Forms with the highest response count</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[250px] w-full" />
            ) : stats.most_active_forms.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                No forms yet
              </div>
            ) : (
              <div className="space-y-4">
                {stats.most_active_forms.map((form, index) => (
                  <div 
                    key={form.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => onViewResponses(form.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{form.title}</p>
                        <p className="text-xs text-muted-foreground">{form.response_count} responses</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Forms */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Recent Forms</CardTitle>
              <CardDescription>Your recently created forms</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => onCreateForm()}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No forms created yet</p>
              <Button variant="outline" className="mt-3" onClick={onCreateForm}>
                Create Your First Form
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {forms.slice(0, 5).map(form => (
                <div 
                  key={form.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium">{form.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant={form.status === 'LIVE' ? 'default' : 'secondary'}
                          className={
                            form.status === 'LIVE' ? 'bg-green-100 text-green-700' :
                            form.status === 'DRAFT' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }
                        >
                          {form.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {form.response_count || 0} responses
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onViewForm(form.id)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onViewResponses(form.id)}>
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FormBuilderDashboard;
